/**
 * MemoForm's on-open scroll + focus behavior, kept in one place so the three
 * entry points into the editor can't drift:
 *  - a click in the read BODY (caret point captured): the Plate editor focuses
 *    itself at the click position — don't steal focus to the title;
 *  - a click in the read TITLE (caret offset captured): focus the title and
 *    drop the caret at the clicked character rather than selecting all;
 *  - the header "Edit note" button (neither): focus + select the whole title.
 *
 * Runs once per mount (the deps are all open-time facts, stable for the life
 * of an editor session).
 */

import type { RefObject } from 'react';
import { useEffect } from 'react';

export interface UseMemoFormAutofocusOptions {
  /** Inline (record-slideout) variant — see the scroll note below. */
  isInline: boolean;
  /** Viewport coords of the read-body click that opened the editor, if any. */
  initialCaretPoint: { x: number; y: number } | null | undefined;
  /** Character offset of the read-title click that opened the editor, if any. */
  initialTitleCaretOffset: number | null | undefined;
  /** Whether a title input exists to focus at all (notes often have none). */
  showTitleInput: boolean;
  titleInputRef: RefObject<HTMLInputElement | null>;
  formRef: RefObject<HTMLFormElement | null>;
}

export function useMemoFormAutofocus({
  isInline,
  initialCaretPoint,
  initialTitleCaretOffset,
  showTitleInput,
  titleInputRef,
  formRef,
}: UseMemoFormAutofocusOptions): void {
  useEffect(() => {
    // Inline (record slideout): the note is already in view and its geometry is
    // reserved, so DON'T scroll — a scroll here would shift the body out from
    // under the captured click coords and misplace the caret. The compose/modal
    // flow still scrolls its freshly-mounted form into view.
    if (!isInline) {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    // A body click leaves focus to the editor. With no title input there is
    // nothing for THIS hook to focus (it only knows the title input); the body
    // editor is focused instead by MemoForm passing `autoFocus` to
    // PlateNoteEditor for the titleless, no-caret case — so returning here is
    // correct, not a dropped-focus bug.
    if (initialCaretPoint || !showTitleInput) {
      return;
    }
    titleInputRef.current?.focus();
    if (initialTitleCaretOffset != null) {
      titleInputRef.current?.setSelectionRange(
        initialTitleCaretOffset,
        initialTitleCaretOffset
      );
    } else {
      titleInputRef.current?.select();
    }
  }, [
    isInline,
    initialCaretPoint,
    initialTitleCaretOffset,
    showTitleInput,
    titleInputRef,
    formRef,
  ]);
}
